import type { ClientHomePlan } from "./clientHomePlanTypes";
import type { HomePlanCategory } from "./homePlanTypes";

/**
 * Frontend read seam for the contract-domain fields (customer, sales staff, category) that today
 * live directly on `ClientHomePlan` and will eventually be derived from a contract. Every
 * accessor here is a chokepoint: when that happens, only these functions' internals change, not
 * every component that reads a home plan's customer/category/sales-agent info.
 */

export const HOME_PLAN_CATEGORY_LABELS: Record<HomePlanCategory, string> = {
  prospect: "Prospect",
  contract: "Contract",
  testing: "Testing",
  closed: "Inactive",
};

export function getHomePlanCustomerName(homePlan: Pick<ClientHomePlan, "customerInfo">): string | undefined {
  return homePlan.customerInfo?.name;
}

export function getHomePlanCustomerId(homePlan: Pick<ClientHomePlan, "customerId">): string | null | undefined {
  return homePlan.customerId;
}

export function getHomePlanCustomerCustomFields(
  homePlan: Pick<ClientHomePlan, "customerCustomFields">,
): Record<string, string | null> | null | undefined {
  return homePlan.customerCustomFields;
}

export function getHomePlanCategoryLabelKey(homePlan: Pick<ClientHomePlan, "category">): string | undefined {
  return homePlan.category ? HOME_PLAN_CATEGORY_LABELS[homePlan.category] : undefined;
}

interface NameParts {
  firstName: string | null;
  lastName: string | null;
}

// Mirrors lib/utils/userName.ts's getUserFullName, reimplemented here rather than imported since
// api-types has no dependency on frontend-only lib/ code.
function fullNameOrEmpty(user: Partial<NameParts> | null | undefined): string {
  if (!user) {
    return "";
  }
  if (user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }
  return user.firstName ?? "";
}

export function getHomePlanSalesAgentDisplayName(
  homePlan: { salesStaff?: Partial<NameParts> | null },
  communityDefaultSalesStaff?: Partial<NameParts> | null,
): string | null {
  return fullNameOrEmpty(homePlan.salesStaff) || fullNameOrEmpty(communityDefaultSalesStaff) || null;
}
